import * as React from "react";
import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  BackendFailureAlert,
  Box,
  Button,
  focusRing,
  Grid2 as Grid,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@platform/style";

import { Resource } from "../../../models/Resource";
import { WorkGroup } from "../../../models/WorkGroup";
import { TemplateV2 } from "../../../models/TemplateV2";

interface WorkGroupCreateProps {
  onHandleBack: () => void;
  onHandleNext: () => void;
  onHandleSnackBar?: (message: string, open: boolean) => void;
  onHandleErrorMessage: (message: string | null) => void;
  onGroupUpdate: any;
  refetchGroups: () => void;
  refetchUsers: () => void;
  isLoadingUsers: boolean;
  isErrorUsers: boolean;
  snackBarDetails?: (message: string, open: boolean) => void;
  groups: WorkGroup[];
  selectedTemplate?: TemplateV2;
  resources: Resource[];
}

interface GroupField {
  name: string;
  description: string;
}

export default function WorkGroupCreate(props: Readonly<WorkGroupCreateProps>) {
  const [selectedResources, setSelectedResources] = useState<any[]>([]);
  const [groups, setGroups] = useState<GroupField[]>([{ name: "", description: "" }]);
  const [disableNext, setDisableNext] = useState(true);
  const [disableRemoveGroup, setDisableRemoveGroup] = useState(true);
  const [groupExistsErrorMessage, setGroupExistsErrorMessage] = React.useState<string | null>(null);
  const resources = props.resources.filter(
    (item, index, self) =>
      index === self.findIndex((resource) => resource.FullName === item.FullName)
  );

  const groupLength = groups.length > 1;

  useEffect(() => {
    if (groupLength) {
      for (const { index } of groups.map((group, index) => ({ group, index }))) {
        const hasNonUniqueValues = groups
          .map((item) => item.name)
          .filter((value, index, self) => self.indexOf(value) !== index);

        if (hasNonUniqueValues.length < 1 && validateInput(groups, selectedResources, index)) {
          handleCloseErrorMessage();
          setDisableNext(false);
        } else if (!validateInput(groups, selectedResources, index)) {
          setGroupExistsErrorMessage(`Unable to move to next step until all fields are complete.`);
          setDisableNext(true);
          break;
        } else {
          setGroupExistsErrorMessage(
            `Group with same name of ${hasNonUniqueValues[index]} already exists.`
          );
          setDisableNext(true);
          break;
        }
      }
    } else {
      setDisableRemoveGroup(true);
      handleCloseErrorMessage();

      if (validateInput(groups, selectedResources, 0)) {
        setDisableNext(false);
      } else {
        setDisableNext(true);
      }
    }
  }, [groups, selectedResources, groupLength, disableRemoveGroup]);

  const handleChange = (index: number, field: keyof GroupField, value: string) => {
    const newGroups = [...groups];
    newGroups[index][field] = value;
    setGroups(newGroups);

    if (validateInput(newGroups, selectedResources, index)) {
      setDisableNext(false);
    } else {
      setDisableNext(true);
    }
  };

  const handleChangeResource = (index: number, value: Resource[] | null) => {
    const newResourceIds = [...selectedResources];
    if (value) {
      newResourceIds[index] = value.map((resource) => resource);
      setSelectedResources(newResourceIds);
    } else {
      setSelectedResources([]);
    }

    if (validateInput(groups, selectedResources, index)) {
      setDisableNext(false);
    } else {
      setDisableNext(true);
    }
  };

  const handleSubmit = async () => {
    const auditInfo: any = {};

    const newGroups: WorkGroup[] = groups.map((field, index) => ({
      WorkGroupIdentifier: "",
      Name: field.name,
      Description: field.description,
      Resources: selectedResources[index] || [],
      Templates: [],
      Audit: auditInfo,
    }));

    let groupExists;
    let hasNonUniqueValues;

    if (newGroups) {
      for (const { group, index } of newGroups.map((group, index) => ({ group, index }))) {
        hasNonUniqueValues = groups
          .map((item) => item.name.trimEnd())
          .filter((value, index, self) => self.indexOf(value.trimEnd()) !== index);

        groupExists = props.groups.find(
          (existingGroup) => group.Name.toLowerCase() === existingGroup.Name.toLowerCase()
        );

        if (groupExists) {
          setDisableNext(true);
          setGroupExistsErrorMessage(`Group with same name of ${group?.Name} already exists.`);
          break;
        } else if (hasNonUniqueValues.length > 0) {
          setDisableNext(true);
          setGroupExistsErrorMessage(
            `Group with same name of ${hasNonUniqueValues[index]} already exists.`
          );
          break;
        }
      }

      if (!groupExists && hasNonUniqueValues && hasNonUniqueValues.length < 1) {
        const templateGroups = props.groups.filter((group) =>
          props.selectedTemplate?.WorkGroups.some(
            (template) => template.WorkGroupIdentifier == group.WorkGroupIdentifier
          )
        );

        const mergedGroups = [...templateGroups, ...newGroups];
        props.onGroupUpdate(mergedGroups);

        setGroups([]);
        setSelectedResources([]);

        props.onHandleNext();
      }
    }
  };

  const handleCloseErrorMessage = () => {
    setGroupExistsErrorMessage(null);
  };

  const handleAddGroup = () => {
    setGroups([...groups, { name: "", description: "" }]);
    setDisableNext(true);
    setDisableRemoveGroup(false);
  };

  const handleRemoveGroup = () => {
    if (!disableRemoveGroup) {
      setGroups(groups.slice(0, -1));
    }
  };

  const validateInput = (groups: GroupField[], selectedResources: any[], index: number) => {
    return (
      groups[index]["name"] && groups[index]["description"] && selectedResources[index]?.length > 0
    );
  };

  return (
    <>
      {props.isLoadingUsers ? (
        <LinearProgress sx={{ mt: 1 }} />
      ) : (
        <Box sx={{ mt: 1, height: "4px" }}></Box>
      )}
      {props.isErrorUsers ? (
        <Box sx={{ mt: 1 }}>
          <BackendFailureAlert retryFunction={props.refetchUsers} />
        </Box>
      ) : (
        <Paper
          elevation={0}
          sx={{
            backgroundColor: "inherit",
            flexDirection: "column",
            overflowX: "hidden",
            overflowY: "auto",
            paddingLeft: "8px",
            paddingRight: "8px",
            paddingBottom: "8px",
          }}
        >
          {groupExistsErrorMessage && (
            <Alert
              onClose={handleCloseErrorMessage}
              severity="error"
              sx={{
                width: "calc(100% - 34px)",
                borderColor: "error.main",
                borderWidth: "1px",
                borderStyle: "solid",
              }}
            >
              {groupExistsErrorMessage}
            </Alert>
          )}
          {groups.map((group, index) => (
            <div key={index}>
              <Typography
                aria-label={"Group Create Header"}
                variant="h6"
                sx={{ paddingTop: "20px", paddingBottom: "10px" }}
              >
                Create New Work Group
              </Typography>
              <Grid container>
                <Grid size={{ sm: 5, xs: 11 }}>
                  <TextField
                    sx={{ display: "flex" }}
                    aria-label={"Name " + index}
                    label="Name"
                    variant="outlined"
                    id="groupName"
                    value={group.name}
                    onChange={(e) => handleChange(index, "name", e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
                <Grid size={{ sm: 5, xs: 11 }} sx={{ marginLeft: "auto" }}>
                  <TextField
                    sx={{ display: "flex" }}
                    aria-label={"Description " + index}
                    label="Description"
                    variant="outlined"
                    id="groupDescription"
                    value={group.description}
                    onChange={(e) => handleChange(index, "description", e.target.value)}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Grid>
              </Grid>
              <Typography variant="h6" sx={{ paddingTop: "20px", paddingBottom: "10px" }}>
                Add People to this Group
              </Typography>
              <Grid size={{ xs: 12 }} sx={{ paddingBottom: "20px" }}>
                <Autocomplete
                  id={"addPeople " + index}
                  aria-description={"Selects User(s) for Groups " + index}
                  aria-label={"Resource Search Autocomplete " + index}
                  multiple
                  limitTags={5}
                  options={resources}
                  noOptionsText={"No Resource found"}
                  getOptionLabel={(option) => option.FullName}
                  onChange={(_event, value) => handleChangeResource(index, value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      aria-label={"Resource Search " + index}
                      label="Resource Search"
                      placeholder="Select a Resource(s)"
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <li aria-label={key + " " + index} key={key} {...optionProps}>
                        <Stack>
                          <Typography>{option.FullName}</Typography>
                          <Typography variant="caption">{option.UserName}</Typography>
                        </Stack>
                      </li>
                    );
                  }}
                />
              </Grid>
            </div>
          ))}
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", bottom: "60px" }}>
            <Button
              aria-label={"Add New Group"}
              sx={{ backgroundColor: "rgb(113, 197, 232)" }}
              variant="contained"
              color={"secondary"}
              onClick={handleAddGroup}
            >
              Add New Group
            </Button>
            <Button
              aria-label={"Remove Group"}
              sx={{ backgroundColor: "rgb(113, 197, 232)" }}
              variant="contained"
              color={"secondary"}
              disabled={disableRemoveGroup}
              onClick={handleRemoveGroup}
            >
              Remove New Group
            </Button>
          </Stack>
          <Grid container sx={{ paddingTop: 2, justifyContent: "flex-end" }}>
            <Box
              sx={{
                bottom: "60px",
                display: "flex",
                flexDirection: "row",
                pt: 2,
              }}
            >
              <Button
                aria-label={"Group Create Back"}
                variant="outlined"
                color={"primary"}
                sx={{ focusRing, marginRight: "5px" }}
                onClick={props.onHandleBack}
              >
                Back
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />
              <Button
                aria-label={"Group Create Next"}
                variant="contained"
                color={"primary"}
                sx={focusRing}
                disabled={disableNext || groupExistsErrorMessage != null}
                onClick={handleSubmit}
              >
                Next
              </Button>
            </Box>
          </Grid>
        </Paper>
      )}
    </>
  );
}
